import axios from "axios";

const getApiBaseURL = () => {
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== "/api") {
    return process.env.REACT_APP_API_URL;
  }

  if (window.location.hostname === "212.113.122.144") {
    return `${window.location.protocol}//${window.location.hostname}:8000/api`;
  }

  return "/api";
};

export const api = axios.create({
  baseURL: getApiBaseURL(),
  paramsSerializer: (params) => {
    const parts = [];

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (Array.isArray(value)) {
        value.forEach((item) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        });
        return;
      }

      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });

    return parts.join("&");
  },
});

export const baseURL = api.defaults.baseURL;

const isDevBypassEnabled = process.env.REACT_APP_DEV_BYPASS_AUTH === "true";
const devAutoEmail = process.env.REACT_APP_DEV_AUTO_EMAIL || "dev@example.com";
const devAutoRole = process.env.REACT_APP_DEV_AUTO_ROLE || "admin";

const emailToDisplay = (email) => (email ? email.split("@")[0] : "");

export const isAuthenticated = () => {
  if (isDevBypassEnabled) {
    return true;
  }

  const email = localStorage.getItem("auth_email");
  const password = localStorage.getItem("auth_password");
  return Boolean(email && password);
};

export const setBasicAuth = (email, password) => {
  if (email && password) {
    const token = btoa(`${email}:${password}`);
    api.defaults.headers.common.Authorization = `Basic ${token}`;
    localStorage.setItem("auth_email", email);
    localStorage.setItem("auth_password", password);
    // Clean up old keys if present
    localStorage.removeItem("auth_username");
    return;
  }

  delete api.defaults.headers.common.Authorization;
  localStorage.removeItem("auth_email");
  localStorage.removeItem("auth_password");
  localStorage.removeItem("auth_username");
  localStorage.removeItem("user_role");
  localStorage.removeItem("user_name");
};

export const setUserRole = (role) => {
  if (role) {
    localStorage.setItem("user_role", role);
    return;
  }

  localStorage.removeItem("user_role");
};

export const getUserRole = () => {
  if (isDevBypassEnabled) {
    return devAutoRole;
  }

  return localStorage.getItem("user_role") || "user";
};

export const getUserName = () => {
  if (isDevBypassEnabled) {
    return emailToDisplay(devAutoEmail);
  }

  const email = localStorage.getItem("auth_email") || "";
  return emailToDisplay(email);
};

// Идентификатор текущего аккаунта для локальных настроек интерфейса (не секрет).
export const getAuthEmail = () => {
  if (isDevBypassEnabled) {
    return devAutoEmail;
  }

  return localStorage.getItem("auth_email") || "";
};

export const isAdmin = () => getUserRole() === "admin";

// Единая точка оповещения о смене состояния авторизации.
export const notifyAuthChange = () => {
  window.dispatchEvent(new Event("auth-change"));
};

// Полный выход: чистим креды/роль/имя и оповещаем подписчиков (охранники
// маршрутов сами уводят на /login).
export const logout = () => {
  setBasicAuth(null, null);
  notifyAuthChange();
};

// Restore auth on page load — try new key first, fall back to old key for migration
const savedEmail = localStorage.getItem("auth_email") || localStorage.getItem("auth_username");
const savedPassword = localStorage.getItem("auth_password");

if (!isDevBypassEnabled && savedEmail && savedPassword) {
  setBasicAuth(savedEmail, savedPassword);
}

// Публичные эндпоинты — им заглушка Authorization не нужна (и может помешать).
const PUBLIC_AUTH_PATHS = [
  "/auth/register",
  "/auth/password/forgot",
  "/auth/password/reset",
];

const isPublicAuthPath = (url = "") =>
  PUBLIC_AUTH_PATHS.some((path) => url.includes(path));

// Всегда отправляем Authorization на защищённые запросы. Браузер показывает
// встроенное окно Basic-аутентификации только на 401 БЕЗ Authorization в
// запросе (same-origin, как на домене). Если реальных кредов нет — ставим
// заведомо неверную заглушку: сервер вернёт обычный JSON-401, SPA обработает
// его сама, а нативное окно не всплывёт.
const ANONYMOUS_BASIC = `Basic ${btoa("anonymous:")}`;

api.interceptors.request.use((config) => {
  if (isDevBypassEnabled) {
    return config;
  }

  const hasAuth =
    config.headers?.Authorization ||
    api.defaults.headers.common?.Authorization;

  if (!hasAuth && !isPublicAuthPath(config.url)) {
    config.headers = config.headers || {};
    config.headers.Authorization = ANONYMOUS_BASIC;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isDevBypassEnabled) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      setBasicAuth(null, null);
      window.dispatchEvent(new Event("auth-change"));
    }

    return Promise.reject(error);
  }
);

export default api;
